import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../api.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { LastUsedPill, OAuthButtons } from '../components/OAuthButtons.js';
import { Button, Field, inputClass } from '../components/ui.js';
import { requestOtp, verifyOtp } from '../lib/auth.js';
import { getLastAuthMethod, setLastAuthMethod } from '../lib/lastAuth.js';

export function Login() {
  const { baseUrl, signIn } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [lastUsed] = useState(getLastAuthMethod);

  const requestCode = async () => {
    setBusy(true);
    setError(null);
    try {
      await requestOtp(baseUrl, email);
      setStep('code');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send code');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await verifyOtp(baseUrl, email, code);
      setLastAuthMethod('otp'); // remember the method for the next visit
      if (res.account?.status === 'suspended') {
        setDisabled(true);
        return;
      }
      if (!res.account || res.account.status !== 'active') {
        setPending(true);
        return;
      }
      signIn(res.token);
      navigate('/');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  if (disabled) {
    return (
      <AuthLayout title="Account disabled">
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          This account has been disabled by CommissionSight. Your data is preserved and nothing has
          been deleted — contact{' '}
          <a className="font-medium underline" href="mailto:support@commissionsight.com">
            support@commissionsight.com
          </a>{' '}
          to restore access.
        </div>
      </AuthLayout>
    );
  }

  if (pending) {
    return (
      <AuthLayout title="Account pending approval">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          Thanks for signing up. Your account is awaiting approval — we'll email{' '}
          <span className="font-medium">{email}</span> the moment access is granted.
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle={
        step === 'email'
          ? "Enter your email and we'll send a one-time code."
          : `We sent a 6-digit code to ${email}.`
      }
      footer={
        <>
          New to CommissionSight?{' '}
          <Link to="/register" className="font-medium text-emerald-600 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      {error && (
        <p className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
      )}
      {step === 'email' && <OAuthButtons verb="Sign in" lastUsed={lastUsed} />}
      {step === 'email' ? (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (email) requestCode();
          }}
        >
          <Field label="Email">
            <input
              className={inputClass}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Field>
          <div className="relative">
            <Button type="submit" className="w-full" loading={busy} disabled={!email}>
              {busy ? 'Sending…' : 'Send code'}
            </Button>
            {lastUsed === 'otp' && <LastUsedPill />}
          </div>
        </form>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (code) verify();
          }}
        >
          <Field label="Verification code">
            <input
              className={`${inputClass} text-center text-lg tracking-[0.4em]`}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="••••••"
              inputMode="numeric"
              maxLength={6}
            />
          </Field>
          <Button type="submit" className="w-full" loading={busy} disabled={!code}>
            {busy ? 'Verifying…' : 'Verify & sign in'}
          </Button>
          <button
            type="button"
            onClick={() => {
              setStep('email');
              setCode('');
            }}
            className="w-full text-center text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
          >
            Use a different email
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
